<template>
  <Modal v-if="visible" :TogglePopup="togglePopup">
    <div class="user-details">
      <header class="user-details-header">
        <div>
          <h1>{{ user.name || 'Unnamed user' }}</h1>
          <div class="user-details-contact">
            <span>{{ user.cellnumber || 'No phone number' }}</span>
            <a v-if="user.email" :href="`mailto:${user.email}`">{{ user.email }}</a>
          </div>
          <p class="user-details-created">Joined {{ user.created_date || '-' }}</p>
        </div>
        <button type="button" class="btn user-details-sms-button" :disabled="!user.cellnumber" @click="showSmsComposer = true">
          Send SMS
        </button>
      </header>

      <section class="user-details-section">
        <div class="user-details-section-heading"><h2>Groups</h2></div>
        <div class="user-details-summary">
          <div><span>Groups created</span><strong>{{ user.created_hunts }}</strong></div>
          <div><span>Groups joined</span><strong>{{ user.all_hunts }}</strong></div>
          <div><span>Last group</span><strong>{{ user.last_joined_date || 'None' }}</strong></div>
        </div>
      </section>

      <section class="user-details-section">
        <div class="user-details-section-heading"><h2>Places</h2></div>
        <div v-if="loadingPlaces" class="text-muted">Loading places...</div>
        <div v-else-if="placesError" class="text-danger">{{ placesError }}</div>
        <div v-else class="user-details-summary">
          <div><span>Created by user</span><strong>{{ places.created }}</strong></div>
          <div><span>Shared with user</span><strong>{{ places.shared }}</strong></div>
          <div><span>Total active places</span><strong>{{ places.total }}</strong></div>
        </div>
      </section>

      <section class="user-details-section">
      <div class="user-details-section-heading"><h2>Teams <span>({{ Array.isArray(user.teamsinfo) ? user.teamsinfo.length : 0 }})</span></h2></div>
      <div class="user-details-table-wrap">
      <table class="table table-hover user-details-table">
        <thead>
          <tr>
            <th class="bold">Team name</th>
            <th class="bold">Role</th>
            <th class="bold">Team owner</th>
            <th class="bold">Team owner number</th>
            <th class="bold">Details</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="teamInfo in user.teamsinfo" :key="`${teamInfo.teamName}-${teamInfo.teamRole}-${teamInfo.teamOwnerNumber}`">
            <th>{{ teamInfo.teamName }}</th>
            <th>{{ teamInfo.teamRole }}</th>
            <th>{{ teamInfo.teamOwner }}</th>
            <th>{{ teamInfo.teamOwnerNumber }}</th>
            <td>
              <button
                type="button"
                class="btn btn-sm user-details-open-team"
                :disabled="!teamInfo.teamName"
                @click="openTeamDetails(teamInfo)"
              >
                Open team
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
      </section>

      <section class="user-details-section">
      <div class="user-details-section-heading"><h2>Trackers <span>({{ trackers.length }})</span></h2></div>
      <div v-if="loadingTrackers" class="text-muted">Loading trackers...</div>
      <div v-else-if="trackersError" class="text-danger">{{ trackersError }}</div>
      <div v-else-if="trackers.length === 0" class="text-muted">No trackers found.</div>
      <div v-else class="user-details-table-wrap">
      <table class="table table-hover user-details-table">
        <thead>
          <tr>
            <th class="bold">SIM number</th>
            <th class="bold">Valid until</th>
            <th class="bold">Status</th>
            <th class="bold">IMEI</th>
            <th class="bold">Brand</th>
            <th class="bold">Role</th>
            <th class="bold">Name</th>
            <th class="bold">Owner</th>
            <th class="bold">Owner number</th>
            <th class="bold">Shared with</th>
            <th class="bold">Notes</th>
            <th class="bold">Details</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(tracker, index) in trackers" :key="tracker.imei || index">
            <td>{{ tracker.cellNumber || '-' }}</td>
            <td><span :class="getValidUntilClass(tracker.sim?.validUntil)">{{ formatTableDate(tracker.sim?.validUntil) || '-' }}</span></td>
            <td><span :class="getStatusClass(tracker.sim?.status)">{{ tracker.sim?.status || '-' }}</span></td>
            <td>{{ tracker.imei || '-' }}</td>
            <td><span :class="getBrandClass(tracker.sim?.brand)">{{ tracker.sim?.brand || '-' }}</span></td>
            <td>{{ tracker.role || '-' }}</td>
            <td>{{ tracker.name || '-' }}</td>
            <td>{{ tracker.owner || '-' }}</td>
            <td>{{ tracker.ownerPhone || '-' }}</td>
            <td>{{ tracker.sharedPhones || '-' }}</td>
            <td>{{ tracker.sim?.notes || '-' }}</td>
            <td><button type="button" class="btn user-details-tracker-button" title="Open tracker details" aria-label="Open tracker details" @click="openTrackerDetails(tracker)"><svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" fill="currentColor" class="bi bi-info-lg" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm0 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14z"/><path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588z"/><circle cx="8" cy="4.5" r="1"/></svg></button></td>
          </tr>
        </tbody>
      </table>
      </div>
      </section>

      <section class="user-details-section">
      <div class="user-details-section-heading"><h2>Licenses <span>({{ Array.isArray(user.licenses) ? user.licenses.length : 0 }})</span></h2></div>

      <div class="user-details-table-wrap">
      <table class="table table-hover user-details-table">
        <thead>
          <tr>
            <th class="bold">#</th>
            <th class="bold">Comment</th>
            <th class="bold">Creator</th>
            <th class="bold">Product</th>
            <th class="bold">Created</th>
            <th class="bold">Valid until</th>
          </tr>
        </thead>
        <tbody v-if="!togglelicenses && Array.isArray(user.licenses) && user.licenses.length > 0">
          <tr>
            <th>1</th>
            <th>{{ longestValidLicense.comment }}</th>
            <th>{{ longestValidLicense.creator }}</th>
            <th>{{ longestValidLicense.product }}</th>
            <th>{{ formatTableDate(longestValidLicense.created) }}</th>
            <th>{{ formatTableDate(longestValidLicense.validuntil ?? longestValidLicense.validUntil) }}</th>
          </tr>
        </tbody>
        <tbody v-else-if="togglelicenses && Array.isArray(user.licenses)">
          <tr v-for="(license, index) in licensesNewestFirst" :key="`${license.created}-${index}`">
            <th scope="row">{{ index + 1 }}</th>
            <th scope="row">{{ license.comment }}</th>
            <th scope="row">{{ license.creator }}</th>
            <th scope="row">{{ license.product }}</th>
            <th scope="row">{{ formatTableDate(license.created) }}</th>
            <th scope="row">{{ formatTableDate(license.validuntil) }}</th>
          </tr>
        </tbody>
      </table>
      </div>

      <div v-if="togglenewlicense" class="mt-2 license-form">
        <p>Date</p>
        <input v-model="userLicenseDate" type="date" class="form-control" />
        <p>Comment</p>
        <input id="newLicenseComment" type="text" class="form-control" />
        <button type="button" class="btn btn-outline-dark btn-rounded mt-2" @click="submitSingleLicense(user)">Save License</button>
      </div>
      <button v-else type="button" class="btn btn-outline-dark btn-rounded mt-2" @click="toggleUserLicense()">
        Add License
      </button>

      <div class="row justify-content-center" v-if="!togglelicenses">
        <svg v-on:click="toggleLicenses()" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-arrows-expand" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 0 1h-13A.5.5 0 0 1 1 8zM7.646.146a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1-.708.708L8.5 1.707V5.5a.5.5 0 0 1-1 0V1.707L6.354 2.854a.5.5 0 1 1-.708-.708l2-2zM8 10a.5.5 0 0 1 .5.5v3.793l1.146-1.147a.5.5 0 0 1 .708.708l-2 2a.5.5 0 0 1-.708 0l-2-2a.5.5 0 0 1 .708-.708L7.5 14.293V10.5A.5.5 0 0 1 8 10z" /></svg>
      </div>
      <div class="row justify-content-center" v-else>
        <svg v-on:click="toggleLicenses()" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-arrows-collapse" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 0 1h-13A.5.5 0 0 1 1 8zm7-8a.5.5 0 0 1 .5.5v3.793l1.146-1.147a.5.5 0 0 1 .708.708l-2 2a.5.5 0 0 1-.708 0l-2-2a.5.5 0 1 1 .708-.708L7.5 4.293V.5A.5.5 0 0 1 8 0zm-.5 11.707-1.146 1.147a.5.5 0 0 1-.708-.708l2-2a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1-.708.708L8.5 11.707V15.5a.5.5 0 0 1-1 0v-3.793z" /></svg>
      </div>

      </section>

      <section class="user-details-section">
      <div class="user-details-section-heading"><h2>Devices <span>({{ Array.isArray(user.devices) ? user.devices.length : 0 }})</span></h2></div>
      <div class="user-details-table-wrap">
      <table class="table table-hover user-details-table">
        <thead>
          <tr>
            <th class="bold">#</th>
            <th class="bold">App Language</th>
            <th class="bold">App Version</th>
            <th class="bold">Created</th>
            <th class="bold">Email</th>
            <th class="bold">Manufacturer</th>
            <th class="bold">Model</th>
            <th class="bold">Name</th>
            <th class="bold">OS</th>
            <th class="bold">OS Version</th>
          </tr>
        </thead>
        <tbody v-if="!toggledevices && Array.isArray(user.devices) && user.devices.length > 0">
          <tr>
            <th>1</th>
            <th>{{ devicesNewestFirst[0].applanguage }}</th>
            <th>{{ devicesNewestFirst[0].appversion }}</th>
            <th>{{ formatTableDate(devicesNewestFirst[0].created) }}</th>
            <th>{{ devicesNewestFirst[0].email }}</th>
            <th>{{ devicesNewestFirst[0].manufacturer }}</th>
            <th>{{ devicesNewestFirst[0].model }}</th>
            <th>{{ devicesNewestFirst[0].name }}</th>
            <th>{{ devicesNewestFirst[0].opsys }}</th>
            <th>{{ devicesNewestFirst[0].opsysversion }}</th>
          </tr>
        </tbody>
        <tbody v-else-if="toggledevices && Array.isArray(user.devices)">
          <tr v-for="(device, index) in devicesNewestFirst" :key="`${device.created}-${index}`">
            <th scope="row">{{ index + 1 }}</th>
            <th scope="row">{{ device.applanguage }}</th>
            <th scope="row">{{ device.appversion }}</th>
            <th scope="row">{{ formatTableDate(device.created) }}</th>
            <th scope="row">{{ device.email }}</th>
            <th scope="row">{{ device.manufacturer }}</th>
            <th scope="row">{{ device.model }}</th>
            <th scope="row">{{ device.name }}</th>
            <th scope="row">{{ device.opsys }}</th>
            <th scope="row">{{ device.opsysversion }}</th>
          </tr>
        </tbody>
      </table>
      </div>

      <div class="row justify-content-center" v-if="!toggledevices">
        <svg v-on:click="toggleDevices()" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-arrows-expand" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 0 1h-13A.5.5 0 0 1 1 8zM7.646.146a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1-.708.708L8.5 1.707V5.5a.5.5 0 0 1-1 0V1.707L6.354 2.854a.5.5 0 1 1-.708-.708l2-2zM8 10a.5.5 0 0 1 .5.5v3.793l1.146-1.147a.5.5 0 0 1 .708.708l-2 2a.5.5 0 0 1-.708 0l-2-2a.5.5 0 0 1 .708-.708L7.5 14.293V10.5A.5.5 0 0 1 8 10z" /></svg>
      </div>
      <div class="row justify-content-center" v-else>
        <svg v-on:click="toggleDevices()" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-arrows-collapse" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 0 1h-13A.5.5 0 0 1 1 8zm7-8a.5.5 0 0 1 .5.5v3.793l1.146-1.147a.5.5 0 0 1 .708.708l-2 2a.5.5 0 0 1-.708 0l-2-2a.5.5 0 1 1 .708-.708L7.5 4.293V.5A.5.5 0 0 1 8 0zm-.5 11.707-1.146 1.147a.5.5 0 0 1-.708-.708l2-2a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1-.708.708L8.5 11.707V15.5a.5.5 0 0 1-1 0v-3.793z" /></svg>
      </div>

      </section>

      <section class="user-details-section">
      <div class="user-details-section-heading"><h2>Notes <span>({{ Array.isArray(user.notes) ? user.notes.length : 0 }})</span></h2></div>
      <div class="user-details-table-wrap">
      <table class="table table-hover user-details-table">
        <thead>
          <tr>
            <th class="bold">#</th>
            <th class="bold">Created</th>
            <th class="bold">Comment</th>
          </tr>
        </thead>
        <tbody v-if="!togglenotes && Array.isArray(user.notes) && user.notes.length > 0">
          <tr>
            <th>1</th>
            <th>{{ formatTableDate(notesNewestFirst[0].created) }}</th>
            <th v-if="notesNewestFirst[0].data.text != null">{{ notesNewestFirst[0].data.text }}</th>
            <th scope="row" v-else-if="notesNewestFirst[0].data.text == null && notesNewestFirst[0].name == 'huntloc_login'">Huntloc Login</th>
            <th scope="row" v-else-if="notesNewestFirst[0].data.text == null && notesNewestFirst[0].name == 'liveteam_login'">Liveteam Login</th>
            <th scope="row" v-else></th>
          </tr>
        </tbody>
        <tbody v-else-if="togglenotes && Array.isArray(user.notes)">
          <tr v-for="(note, index) in notesNewestFirst" :key="`${note.created}-${index}`">
            <th scope="row">{{ index + 1 }}</th>
            <th>{{ formatTableDate(note.created) }}</th>
            <th scope="row" v-if="note.data.text != null">{{ note.data.text }}</th>
            <th scope="row" v-else-if="note.data.text == null && note.name == 'huntloc_login'">Huntloc Login</th>
            <th scope="row" v-else-if="note.data.text == null && note.name == 'liveteam_login'">Liveteam Login</th>
            <th scope="row" v-else></th>
          </tr>
        </tbody>
      </table>
      </div>

      <div class="row justify-content-center" v-if="!togglenotes">
        <svg v-on:click="toggleNotes()" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-arrows-expand" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 0 1h-13A.5.5 0 0 1 1 8zM7.646.146a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1-.708.708L8.5 1.707V5.5a.5.5 0 0 1-1 0V1.707L6.354 2.854a.5.5 0 1 1-.708-.708l2-2zM8 10a.5.5 0 0 1 .5.5v3.793l1.146-1.147a.5.5 0 0 1 .708.708l-2 2a.5.5 0 0 1-.708 0l-2-2a.5.5 0 0 1 .708-.708L7.5 14.293V10.5A.5.5 0 0 1 8 10z" /></svg>
      </div>
      <div class="row justify-content-center" v-else>
        <svg v-on:click="toggleNotes()" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-arrows-collapse" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 0 1h-13A.5.5 0 0 1 1 8zm7-8a.5.5 0 0 1 .5.5v3.793l1.146-1.147a.5.5 0 0 1 .708.708l-2 2a.5.5 0 0 1-.708 0l-2-2a.5.5 0 1 1 .708-.708L7.5 4.293V.5A.5.5 0 0 1 8 0zm-.5 11.707-1.146 1.147a.5.5 0 0 1-.708-.708l2-2a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1-.708.708L8.5 11.707V15.5a.5.5 0 0 1-1 0v-3.793z" /></svg>
      </div>

      </section>

      <section class="user-details-section">
        <div class="user-details-section-heading"><h2>Add note</h2></div>
        <textarea v-model="noteText" style="height: 100px" class="form-control note-input" placeholder="Write a note for this user..." />
        <div class="d-flex gap-2 mt-2">
          <button type="button" class="btn btn-outline-dark btn-rounded" :disabled="savingNote" @click="addNotes(user)">
            {{ savingNote ? 'Saving...' : 'Save note' }}
          </button>
          <button
            type="button"
            class="btn btn-outline-primary btn-rounded"
            :disabled="savingNote || !user.email"
            :title="user.email ? '' : 'User has no email address'"
            @click="addNotes(user, true)"
          >
            Save note and open email
          </button>
        </div>
      </section>

      <section class="user-details-section">
        <div class="user-details-section-heading"><h2>Portal Authentication</h2></div>
        <div class="user-details-portal-controls">
          <input type="text" class="form-control portal-pin" placeholder="PIN" v-model="portalPIN"/>
          <button type="button" class="btn btn-outline-dark btn-rounded" data-mdb-ripple-color="dark" v-on:click="loginPortal(user)">Login</button>
        </div>
      </section>
    </div>
  </Modal>

  <SimDetailsModal
    :visible="showTrackerDetails"
    :sim="selectedTrackerSim"
    :team-display="selectedTrackerSim.pricingModel || ''"
    :toggle-popup="closeTrackerDetails"
    @open-user="openRelatedUserDetails"
    @open-team="openTeamDetails({ teamName: $event })"
  />

  <UserDetailsModal
    v-if="relatedUser"
    :visible="true"
    :user="relatedUser"
    :toggle-popup="() => { relatedUser = null }"
  />

  <Modal v-if="showSmsComposer" :TogglePopup="closeSmsComposer">
    <div class="sms-dialog"><SmsComposer :recipient="user.cellnumber" type="notification" /></div>
  </Modal>
</template>

<script>
import axios from 'axios'
import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import Modal from '@/components/Modal.vue'
import SimDetailsModal from '@/components/SimDetailsModal.vue'
import SmsComposer from '@/components/SmsComposer.vue'
import { API_ENDPOINTS } from '@/config/api'
import { getLongestValidLicense, normalizeAdminUser } from '@/utils/userUtils'

export default {
  name: 'UserDetailsModal',
  components: {
    Modal,
    SimDetailsModal,
    SmsComposer
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    togglePopup: {
      type: Function,
      required: true
    },
    user: {
      type: Object,
      default: () => ({
        teamsinfo: [],
        licenses: [],
        devices: [],
        notes: []
      })
    }
  },
  data() {
    return {
      portalPIN: '',
      togglenotes: false,
      togglelicenses: false,
      toggledevices: false,
      togglenewlicense: false,
      userLicenseDate: 0,
      trackers: [],
      loadingTrackers: false,
      trackersError: '',
      selectedTrackerSim: {},
      relatedUser: null,
      showTrackerDetails: false,
      showSmsComposer: false,
      trackerLookupPhoneNumber: '',
      places: { created: 0, shared: 0, total: 0 },
      loadingPlaces: false,
      placesError: '',
      placesLookupPhoneNumber: '',
      noteText: '',
      savingNote: false
    }
  },
  computed: {
    longestValidLicense() {
      return getLongestValidLicense(this.user.licenses) || this.licensesNewestFirst[0] || {}
    },
    licensesNewestFirst() {
      return this.sortNewestFirst(this.user.licenses)
    },
    devicesNewestFirst() {
      return this.sortNewestFirst(this.user.devices)
    },
    notesNewestFirst() {
      return this.sortNewestFirst(this.user.notes)
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(isVisible) {
      if (isVisible) {
        this.loadTrackers()
        this.loadPlaces()
      } else {
        this.showTrackerDetails = false
        this.showSmsComposer = false
        this.relatedUser = null
        this.resetTrackers()
        this.resetPlaces()
      }
      }
    },
    'user.cellnumber'() {
      if (this.visible) {
        this.loadTrackers()
        this.loadPlaces()
      }
    }
  },
  methods: {
    sortNewestFirst(items) {
      if (!Array.isArray(items)) return []

      return items
        .map((item, index) => ({ item, index }))
        .sort((a, b) => {
          const aCreated = new Date(a.item?.created).getTime() || 0
          const bCreated = new Date(b.item?.created).getTime() || 0
          return bCreated - aCreated || a.index - b.index
        })
        .map(({ item }) => item)
    },
    getValidUntilClass(validUntil) {
      if (!validUntil) return ''
      const validDate = new Date(validUntil)
      if (Number.isNaN(validDate.getTime())) return ''

      validDate.setHours(23, 59, 59, 999)
      const todayStart = new Date()
      todayStart.setHours(0, 0, 0, 0)
      const expiresSoonEnd = new Date(todayStart)
      expiresSoonEnd.setDate(expiresSoonEnd.getDate() + 14)
      expiresSoonEnd.setHours(23, 59, 59, 999)

      if (validDate.getTime() < todayStart.getTime()) return 'sim-valid-expired'
      if (validDate.getTime() <= expiresSoonEnd.getTime()) return 'sim-valid-soon'
      return ''
    },
    getStatusClass(status) {
      if (status === 'Stopped') return 'stopped'
      if (status === 'Closed') return 'closed'
      return ''
    },
    getBrandClass(brand) {
      if (String(brand || '').trim() === 'Huntloc') return 'sim-brand-huntloc'
      if (String(brand || '').trim() === 'Doglo') return 'sim-brand-doglo'
      return ''
    },
    resetTrackers() {
      this.trackers = []
      this.loadingTrackers = false
      this.trackersError = ''
      this.trackerLookupPhoneNumber = ''
    },
    openTrackerDetails(tracker) {
      this.selectedTrackerSim = {
        ...(tracker?.sim || {}),
        imei: tracker?.imei || tracker?.sim?.imei,
        phoneNumber: tracker?.cellNumber || tracker?.sim?.phoneNumber
      }
      this.showTrackerDetails = true
    },
    closeTrackerDetails() {
      this.showTrackerDetails = false
    },
    openRelatedUserDetails(user) {
      this.showTrackerDetails = false
      this.relatedUser = normalizeAdminUser(user)
    },
    closeSmsComposer() {
      this.showSmsComposer = false
    },
    resetPlaces() {
      this.places = { created: 0, shared: 0, total: 0 }
      this.loadingPlaces = false
      this.placesError = ''
      this.placesLookupPhoneNumber = ''
    },
    async loadTrackers() {
      const phoneNumber = String(this.user?.cellnumber || '').replace(/\s+/g, '')

      if (!phoneNumber || phoneNumber === this.trackerLookupPhoneNumber) return

      this.trackerLookupPhoneNumber = phoneNumber
      this.trackers = []
      this.trackersError = ''
      this.loadingTrackers = true

      try {
        const [response, simsResponse] = await Promise.all([
          axios.get(`${API_ENDPOINTS.TIW_API}/trackers/${encodeURIComponent(phoneNumber)}`),
          axios.get(`${API_ENDPOINTS.TIW_API}/sim/all`)
        ])
        if (this.trackerLookupPhoneNumber !== phoneNumber) return

        const simsByPhoneNumber = new Map(
          (Array.isArray(simsResponse?.data) ? simsResponse.data : [])
            .filter((sim) => sim?.phoneNumber)
            .map((sim) => [String(sim.phoneNumber).replace(/\s+/g, ''), sim])
        )
        this.trackers = (Array.isArray(response?.data) ? response.data : [])
          .filter((tracker) => tracker != null)
          .map((tracker) => ({
            ...tracker,
            sim: simsByPhoneNumber.get(String(tracker.cellNumber || '').replace(/\s+/g, '')) || null
          }))
      } catch (error) {
        if (this.trackerLookupPhoneNumber === phoneNumber) {
          this.trackersError = 'Trackers could not be loaded.'
        }
      } finally {
        if (this.trackerLookupPhoneNumber === phoneNumber) {
          this.loadingTrackers = false
        }
      }
    },
    async loadPlaces() {
      const phoneNumber = String(this.user?.cellnumber || '').replace(/\s+/g, '')

      if (!phoneNumber || phoneNumber === this.placesLookupPhoneNumber) return

      this.placesLookupPhoneNumber = phoneNumber
      this.places = { created: 0, shared: 0, total: 0 }
      this.placesError = ''
      this.loadingPlaces = true

      try {
        const response = await axios.get(`${API_ENDPOINTS.TIW_API}/places/${encodeURIComponent(phoneNumber)}`)
        if (this.placesLookupPhoneNumber !== phoneNumber) return

        const metrics = response?.data || {}
        this.places = {
          created: Number(metrics.created) || 0,
          shared: Number(metrics.shared) || 0,
          total: Number(metrics.total) || 0
        }
      } catch (error) {
        if (this.placesLookupPhoneNumber === phoneNumber) {
          this.placesError = 'Places could not be loaded.'
        }
      } finally {
        if (this.placesLookupPhoneNumber === phoneNumber) {
          this.loadingPlaces = false
        }
      }
    },
    formatTableDate(dateValue) {
      if (dateValue == null) return ''
      const date = new Date(dateValue)
      if (Number.isNaN(date.getTime())) return ''

      const year = date.getFullYear()
      const month = (`0${date.getMonth() + 1}`).slice(-2)
      const day = (`0${date.getDate()}`).slice(-2)

      return `${year}-${month}-${day}`
    },
    async loginPortal(user) {
      const pin = String(this.portalPIN || '').trim()
      const cell = String(user?.cellnumber || '').trim()

      if (!pin) {
        alert('Please enter PIN.')
        return
      }

      if (!cell) {
        alert('User phone number is missing.')
        return
      }

      const apiURL = API_ENDPOINTS.TIW_API + '/portal-auth/' + encodeURIComponent(cell) + '/' + encodeURIComponent(pin)

      try {
        await axios.get(apiURL)
        alert('User signed in')
        this.portalPIN = ''
      } catch (error) {
        const message = error?.response?.data?.message || error?.response?.data?.error || 'Portal login failed.'
        alert(message)
      }
    },
    submitSingleLicense(user) {
      const now = Date.now()
      const email = firebase.auth().currentUser.email
      let name = email.split('@')[0].split('.')[0]
      name = name.charAt(0).toUpperCase() + name.slice(1)
      const comment = document.getElementById('newLicenseComment')
      const time = new Date(this.userLicenseDate)
      const body = {
        created: now,
        creator: name,
        comment: comment.value,
        valid: true,
        validUntil: time.getTime(),
        phoneNumber: user.cellnumber
      }
      const backendAPI = API_ENDPOINTS.TIW_API + '/rest/lic'
      axios.post(backendAPI, body).then(() => {
        body.validuntil = body.validUntil
        user.licenses.push(body)
        alert('License added to ' + user.cellnumber + ' until ' + this.userLicenseDate)
        this.togglenewlicense = !this.togglenewlicense
      })
    },
    async addNotes(user, openEmail = false) {
      const text = this.noteText
      if (!text.trim() || this.savingNote) return

      const body = {
        name: 'admin_note',
        created: Date.now(),
        data: { text },
        dataJsonStr: null
      }

      this.savingNote = true
      try {
        await axios.post(`${API_ENDPOINTS.TIW_API}/activity/${encodeURIComponent(user.cellnumber)}`, body)
        if (!Array.isArray(user.notes)) user.notes = []
        user.notes.push(body)
        this.noteText = ''

        if (openEmail && user.email) {
          window.location.href = `mailto:${encodeURIComponent(user.email)}?body=${encodeURIComponent(text)}`
        }
      } catch (error) {
        alert('Note could not be saved. Please try again.')
      } finally {
        this.savingNote = false
      }
    },
    openTeamDetails(teamInfo) {
      const teamName = String(teamInfo?.teamName || '').trim()
      if (!teamName) return

      this.$router.push({
        name: 'Teams',
        query: {
          team: teamName,
          owner: String(teamInfo?.teamOwnerNumber || '').trim() || undefined
        }
      })
    },
    toggleUserLicense() {
      this.togglenewlicense = !this.togglenewlicense
      const defaultDate = Date.now() + 31556952000
      const year = new Date(defaultDate).getFullYear()
      const month = (`0${new Date(defaultDate).getMonth() + 1}`).slice(-2)
      const day = (`0${new Date(defaultDate).getDate()}`).slice(-2)
      this.userLicenseDate = `${year}-${month}-${day}`
    },
    toggleDevices() {
      this.toggledevices = !this.toggledevices
    },
    toggleLicenses() {
      this.togglelicenses = !this.togglelicenses
    },
    toggleNotes() {
      this.togglenotes = !this.togglenotes
    },
  }
}
</script>

<style scoped>
.bold {
  font-weight: bold;
}

th,
td,
tr,
select,
input,
textarea,
body {
  font-size: 12px;
}

th {
  font-weight: normal;
}

button {
  font-size: 12px;
}

.user-details {
  width: min(1180px, calc(100vw - 48px));
  max-height: calc(100vh - 48px);
  overflow: auto;
  padding: 4px;
  color: #202629;
}

.user-details-header {
  position: sticky;
  top: -4px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: -4px -4px 22px;
  padding: 20px;
  background: #202629;
  border-left: 4px solid #f56600;
  border-radius: 9px;
  box-shadow: none;
}

.user-details-header h1 { margin: 2px 0; font-size: 22px; font-weight: 700; }
.user-details-header h1 { color: #fff; }
.user-details-header span { color: #fff; }
.user-details-contact { display: flex; flex-wrap: wrap; gap: 4px 12px; align-items: center; }
.user-details-contact a { color: #ff8b43; }
.user-details-created { margin: 5px 0 0; color: #c8d0d3; font-size: 11px; font-style: italic; }
.user-details-sms-button { flex: 0 0 auto; border-color: #f56600; background: #f56600; color: #fff; font-size: 12px; }
.user-details-sms-button:hover:not(:disabled) { border-color: #d95700; background: #d95700; color: #fff; }
.sms-dialog { width: min(94vw, 600px); padding: 4px; }
.user-details-section { margin-top: 18px; padding: 14px; border: 1px solid #e1e5e6; border-radius: 9px; background: #fbfcfc; }
.user-details-section-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 10px; }
.user-details-section-heading h2 { margin: 0; color: #202629; font-size: 14px; font-weight: 700; }
.user-details-section-heading h2 span { color: #687277; font-weight: normal; }
.user-details-summary { display: grid; grid-template-columns: repeat(auto-fit, minmax(145px, 1fr)); gap: 8px; }
.user-details-summary > div { min-width: 0; padding: 10px; border-left: 3px solid #f56600; border-radius: 6px; background: #fff; }
.user-details-summary span, .user-details-summary strong { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.user-details-summary span { margin-bottom: 3px; color: #687277; font-size: 10px; }
.user-details-summary strong { color: #202629; font-size: 11px; }
.user-details-table-wrap { overflow-x: auto; border: 1px solid #e1e5e6; border-radius: 7px; background: #fff; }
.user-details-table { min-width: 950px; margin-bottom: 0; }
.user-details-table thead { background: #f1f4f5; }
.user-details-table th, .user-details-table td { padding: 9px 10px; vertical-align: middle; }
.user-details-table thead th { color: #40545e; font-size: 10px; font-weight: 700; letter-spacing: .03em; text-transform: uppercase; }
.user-details-table tbody tr:not(:last-child) > * { border-bottom-color: #e1e5e6 !important; }
.user-details-open-team { border: 1px solid #202629; color: #202629; font-size: 10px; }
.user-details-open-team:hover { border-color: #f56600; background: #f56600; color: #fff; }
.user-details-tracker-button { padding: 2px 4px; color: #202629; }
.user-details-tracker-button:hover { color: #f56600; }
.user-details .btn-outline-dark { border-color: #202629; color: #202629; }
.user-details .btn-outline-dark:hover { border-color: #f56600; background: #f56600; color: #fff; }
.user-details .btn-outline-primary { border-color: #f56600; color: #f56600; }
.user-details .btn-outline-primary:hover { background: #f56600; color: #fff; }
.note-input { min-height: 110px; resize: vertical; }
.license-form { max-width: 360px; }
.license-form p { margin: 10px 0 2px; font-size: 12px; }
.user-details-portal-controls { display: flex; gap: 8px; align-items: center; max-width: 440px; }
.portal-pin { flex: 1; min-width: 0; }

@media (max-width: 700px) {
  .user-details { width: calc(100vw - 24px); max-height: calc(100vh - 24px); }
  .user-details-header { align-items: flex-start; margin-bottom: 18px; }
  .user-details-header h1 { font-size: 18px; }
}

.stopped { background-color: yellow; }
.closed, .sim-valid-expired { background-color: salmon; color: #000; }
.sim-valid-soon { background-color: #ffe066; color: #000; }
.sim-brand-huntloc { background-color: #ff9a00; color: #000; }
.sim-brand-doglo { background-color: #000; color: #fff; }
.sim-valid-expired,
.sim-valid-soon,
.sim-brand-huntloc,
.sim-brand-doglo {
  display: inline-block;
  min-width: 72px;
  padding: 2px 6px;
  border-radius: 999px;
  text-align: center;
}
</style>
